// @mui
import { styled } from '@mui/material/styles';
import { Box, Link, Avatar, Typography, AvatarGroup } from '@mui/material';
// utils
import { fToNowStrict } from 'src/utilities/formatTime';
// components
import Iconify from 'src/minimals/components/Iconify';
import BadgeStatus from 'src/minimals/components/BadgeStatus';
import { useDispatch, useSelector } from 'react-redux';
import dateLanguages from 'src/utilities/dateFnsLocales';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { ChatContext } from '../context/ChatContext';
import CHAT_EVENTS from '../constants/ChatEvents';
import { addMessage, finishConversation } from 'src/redux/slices/chat';
import ChatHeaderActions from './ChatHeaderActions';

export default function ChatHeaderDetail({
  conversation,
  conversationName,
  onAddRecipients,
}) {
  const user = useSelector((state) => state.user);
  const { situation, socket } = useContext(ChatContext);
  const dispatch = useDispatch();
  const isGroup = conversation.participants.length > 2;
  const disabledOptions = !situation && conversation.situationId;

  const handleVerificationRequest = () =>
    new Promise((resolve) => {
      socket.emit(
        CHAT_EVENTS.VERIFICATION_REQUEST,
        { conversationId: conversation.id, situationId: situation.id },
        (response) => resolve(response ?? { success: true })
      );
    });

  const handleFinishConversation = async () => {
    const response = await socket
      .timeout(60 * 1000)
      .emitWithAck(CHAT_EVENTS.CHAT_FINISHED, {
        conversationId: conversation.id,
        userId: user.id,
      });

    if (!response.success) {
      throw new Error('Failed to send message');
    }

    dispatch(finishConversation({ conversationId: response.conversationId }));
    dispatch(
      addMessage({
        conversationId: response.conversationId,
        message: response.message,
      })
    );
  };

  return (
    <RootStyle>
      {isGroup ? (
        <GroupAvatar
          participants={conversation.participants}
          conversationName={conversationName}
        />
      ) : (
        <OneAvatar
          participants={conversation.participants.filter(
            (item) => item.id !== user.id
          )}
        />
      )}
      <Box sx={{ flexGrow: 1 }} />
      {!disabledOptions && !conversation.finished ? (
        <ChatHeaderActions
          conversation={conversation}
          onAddRecipients={onAddRecipients}
          onFinishConversation={handleFinishConversation}
          onVerificationRequest={situation && conversation.situationId ? handleVerificationRequest : null}
        />
      ) : null}
    </RootStyle>
  );
}

function OneAvatar({ participants }) {
  const { t } = useTranslation();
  const { selectedLang } = useSelector((state) => state.language);
  const [participant] = participants;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar src={participant?.profilePicture} alt={participant?.name} />
        <BadgeStatus
          status={participant?.status}
          sx={{ position: 'absolute', right: 2, bottom: 2 }}
        />
      </Box>
      <Box sx={{ ml: 2 }}>
        <Typography variant="subtitle2">
          {participant?.name ?? t('chat.unknown_user_name')}
        </Typography>
        {participant && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {participant?.status !== 'offline'
              ? t('chat.status_online')
              : participant?.lastActivity
                ? t('chat.last_seen', {
                    time: fToNowStrict(participant.lastActivity, {
                      locale: dateLanguages[selectedLang],
                    }),
                  })
                : null}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function GroupAvatar({ participants, conversationName = null }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <AvatarGroup
          max={3}
          sx={{
            mb: 0.5,
            '& .MuiAvatar-root': { width: 32, height: 32 },
          }}
        >
          {participants.map((participant) => (
            <Avatar
              key={participant.id}
              alt={participant.name}
              src={participant.profilePicture}
            />
          ))}
        </AvatarGroup>
        <Typography variant="subtitle2" marginLeft="8px">
          {conversationName ||
            participants
              .map((participant) => participant.name.split(' ')[0])
              .join(', ')}
        </Typography>
      </Box>
      <Link
        variant="body2"
        underline="none"
        component="button"
        color="text.secondary"
        onClick={() => {}}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {t('chat.group_participants_count', {
            total: participants.length,
          })}
          <Iconify icon="eva:arrow-ios-forward-fill" />
        </Box>
      </Link>
    </Box>
  );
}

const RootStyle = styled('div')(({ theme }) => ({
  flexShrink: 0,
  minHeight: 92,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 3),
}));
